import { matchPath } from 'react-router-dom';
import { Route, RefreshCcwDot, Target, Sparkles, WandSparkles } from 'lucide-react';
import { type ReactElement, type SVGProps } from 'react';
import type { NavbarSection } from '../components/layout/Navbar';
import { DASHBOARD_PATH, DEFAULT_DASHBOARD_PATH } from '../config/auth';

export type DashboardSectionKey = 'dashboard' | 'missions' | 'dquest' | 'rewards' | 'editor';

import type { PostLoginLanguage } from '../i18n/postLoginLanguage';
import { resolvePostLoginTranslation } from '../i18n/post-login';

export interface DashboardSectionConfig extends NavbarSection {
  key: DashboardSectionKey;
  pageTitle: string;
  eyebrow?: string;
  contentTitle: string;
  description?: string;
  icon: (props: SVGProps<SVGSVGElement>) => ReactElement;
}

const rawDashboardPath = DASHBOARD_PATH || DEFAULT_DASHBOARD_PATH;
const normalizedDashboardPath = rawDashboardPath.startsWith('/') ? rawDashboardPath : `/${rawDashboardPath}`;
const trimmedDashboardPath = normalizedDashboardPath.replace(/\/+$/, '') || DEFAULT_DASHBOARD_PATH;
const DASHBOARD_BASE_PATH = trimmedDashboardPath;

function isDashboardPath(pathname: string): boolean {
  if (!pathname) {
    return false;
  }

  if (pathname === DASHBOARD_BASE_PATH) {
    return true;
  }

  return pathname.startsWith(`${DASHBOARD_BASE_PATH}/`);
}

function resolveDashboardBasePath(currentPath?: string): string {
  if (currentPath && isDashboardPath(currentPath)) {
    return DASHBOARD_BASE_PATH;
  }

  if (typeof window !== 'undefined' && isDashboardPath(window.location.pathname)) {
    return DASHBOARD_BASE_PATH;
  }

  return DASHBOARD_BASE_PATH;
}

function joinDashboardPath(basePath: string, segment?: string): string {
  if (!segment) {
    return basePath;
  }

  const normalizedSegment = segment.startsWith('/') ? segment : `/${segment}`;
  return `${basePath}${normalizedSegment}`;
}

function buildDashboardSections(basePath: string, language: PostLoginLanguage = 'en'): Record<DashboardSectionKey, DashboardSectionConfig> {
  const t = (key: string) => resolvePostLoginTranslation(language, key);
  return {
    dashboard: {
      key: 'dashboard',
      label: t('dashboard.nav.home'),
      to: joinDashboardPath(basePath),
      end: true,
      pageTitle: t('dashboard.nav.home'),
      contentTitle: t('dashboard.nav.home'),
      icon: (props) => <Target {...props} />,
    },
    missions: {
      key: 'missions',
      label: t('dashboard.section.missions.title'),
      to: joinDashboardPath(basePath, 'misiones'),
      pageTitle: resolvePostLoginTranslation(language, 'dashboard.section.missions.title'),
      eyebrow: t('dashboard.section.missions.title'),
      contentTitle: t('dashboard.section.missions.subtitle'),
      description: t('dashboard.section.missions.description'),
      icon: (props) => <Route {...props} />,
    },
    dquest: {
      key: 'dquest',
      label: 'DQuest',
      to: joinDashboardPath(basePath, 'dquest'),
      end: true,
      pageTitle: t('missions.dailyQuest.title'),
      eyebrow: 'DQuest',
      contentTitle: t('missions.dailyQuest.title'),
      description: t('missions.dailyQuest.description'),
      icon: (props) => <RefreshCcwDot {...props} />,
    },
    rewards: {
      key: 'rewards',
      label: t('dashboard.section.rewards.title'),
      to: joinDashboardPath(basePath, 'rewards'),
      pageTitle: t('dashboard.section.rewards.title'),
      eyebrow: t('dashboard.section.rewards.title'),
      contentTitle: t('dashboard.section.rewards.subtitle'),
      description: t('dashboard.section.rewards.description'),
      icon: (props) => <Sparkles {...props} />,
    },
    editor: {
      key: 'editor',
      label: 'Editor',
      to: '/editor',
      end: true,
      pageTitle: 'Editor',
      eyebrow: 'Editor',
      contentTitle: '',
      description: '',
      icon: (props) => <WandSparkles {...props} />,
    },
  };
}

function createDashboardSections(currentPath?: string, language: PostLoginLanguage = 'en') {
  const basePath = resolveDashboardBasePath(currentPath);
  const sectionsByKey = buildDashboardSections(basePath, language);
  const sections: DashboardSectionConfig[] = [
    sectionsByKey.missions,
    sectionsByKey.dquest,
    sectionsByKey.dashboard,
    sectionsByKey.rewards,
    sectionsByKey.editor,
  ];

  return { sections, sectionsByKey };
}

export function getDashboardSections(currentPath?: string, language: PostLoginLanguage = 'en'): DashboardSectionConfig[] {
  return createDashboardSections(currentPath, language).sections;
}

export function getDashboardSectionConfig(
  key: DashboardSectionKey,
  currentPath?: string,
  language: PostLoginLanguage = 'en',
): DashboardSectionConfig {
  const { sectionsByKey } = createDashboardSections(currentPath, language);
  return sectionsByKey[key];
}

export function isSectionActive(section: DashboardSectionConfig, pathname: string) {
  return matchPath({ path: section.to, end: section.end ?? false }, pathname) != null;
}

export function getActiveSection(
  pathname: string,
  sections: DashboardSectionConfig[] = getDashboardSections(pathname),
  language: PostLoginLanguage = 'en',
): DashboardSectionConfig {
  const activeSection = sections.find((section) => isSectionActive(section, pathname));

  if (activeSection) {
    return activeSection;
  }

  const missionsBasePath = joinDashboardPath(DASHBOARD_BASE_PATH, 'misiones');

  if (pathname.startsWith(missionsBasePath)) {
    const missionsSection = getDashboardSectionConfig('missions', pathname, language);

    return {
      ...missionsSection,
      pageTitle: resolvePostLoginTranslation(language, 'dashboard.section.missions.title'),
    };
  }

  return getDashboardSectionConfig('dashboard', pathname, language);
}
